"use client";

import {
  Package,
  Calendar,
  Truck,
  MapPin,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";

interface ProductSpecsCardProps {
  condition?: string;
  year?: number;
  shippingTypes?: string[];
  region?: string;
  components?: string;
  defects?: string;
}

export function ProductSpecsCard({
  condition,
  year,
  shippingTypes = [],
  region,
  components,
  defects,
}: ProductSpecsCardProps) {
  const hasDirect = shippingTypes.includes("direct");
  const hasCourier =
    shippingTypes.includes("courier") || shippingTypes.includes("parcel");

  const getConditionLabel = (cond?: string) => {
    const labels: Record<string, string> = {
      excellent: "최상",
      good: "상",
      fair: "중",
      poor: "하",
    };
    return labels[cond || ""] || cond || "미기재";
  };

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
      <h2 className="text-xl font-bold text-gray-900 mb-5">상품 상세 정보</h2>

      <div className="space-y-4">
        {/* 상태 */}
        {condition && (
          <div className="flex items-start gap-3">
            <div className="p-2 bg-[#F4F0FF] rounded-xl">
              <Package className="w-5 h-5 text-[#6D4AFF]" />
            </div>
            <div className="flex-1">
              <div className="text-sm text-gray-500 mb-1">상태</div>
              <div className="text-base font-medium text-gray-900">
                {getConditionLabel(condition)}
              </div>
            </div>
          </div>
        )}

        {/* 구매연도 */}
        {year && (
          <div className="flex items-start gap-3">
            <div className="p-2 bg-[#F4F0FF] rounded-xl">
              <Calendar className="w-5 h-5 text-[#6D4AFF]" />
            </div>
            <div className="flex-1">
              <div className="text-sm text-gray-500 mb-1">구매연도</div>
              <div className="text-base font-medium text-gray-900">{year}년</div>
            </div>
          </div>
        )}

        {/* 거래방식 */}
        {(hasDirect || hasCourier) && (
          <div className="flex items-start gap-3">
            <div className="p-2 bg-[#F4F0FF] rounded-xl">
              <Truck className="w-5 h-5 text-[#6D4AFF]" />
            </div>
            <div className="flex-1">
              <div className="text-sm text-gray-500 mb-1">거래방식</div>
              <div className="flex flex-wrap gap-2">
                {hasDirect && (
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                    직거래
                  </span>
                )}
                {hasCourier && (
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                    택배
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 위치 */}
        {region && (
          <div className="flex items-start gap-3">
            <div className="p-2 bg-[#F4F0FF] rounded-xl">
              <MapPin className="w-5 h-5 text-[#6D4AFF]" />
            </div>
            <div className="flex-1">
              <div className="text-sm text-gray-500 mb-1">위치</div>
              <div className="text-base font-medium text-gray-900">
                {region}
                {hasCourier && " / 전국 택배 가능"}
              </div>
            </div>
          </div>
        )}

        {/* 포함품 */}
        {components && (
          <div className="flex items-start gap-3">
            <div className="p-2 bg-[#F4F0FF] rounded-xl">
              <CheckCircle2 className="w-5 h-5 text-[#6D4AFF]" />
            </div>
            <div className="flex-1">
              <div className="text-sm text-gray-500 mb-1">포함품</div>
              <div className="text-base font-medium text-gray-900">
                {components}
              </div>
            </div>
          </div>
        )}

        {/* 하자 여부 */}
        {defects && (
          <div className="flex items-start gap-3">
            <div className="p-2 bg-[#F4F0FF] rounded-xl">
              <AlertCircle className="w-5 h-5 text-[#6D4AFF]" />
            </div>
            <div className="flex-1">
              <div className="text-sm text-gray-500 mb-1">하자 여부</div>
              <div className="text-base font-medium text-gray-900">
                {defects}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

